declare global {
	interface Window {
		clarity?: (action: string, ...args: any[]) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
	}
}

export interface BrowserInfo {
	userAgent: string;
	platform: string;
	vendor: string;
	language: string;
	cookieEnabled: boolean;
	onLine: boolean;
	javaEnabled: boolean;
	features: {
		localStorage: boolean;
		sessionStorage: boolean;
		fetch: boolean;
		promise: boolean;
		urlSearchParams: boolean;
		webSocket: boolean;
		webGL: boolean;
		canvas: boolean;
		svg: boolean;
		flexbox: boolean;
		grid: boolean;
		cssVariables: boolean;
	};
	errors: string[];
}

type FeatureTest = () => boolean;

function testFeature(name: string, test: FeatureTest, errors: string[]): boolean {
	try {
		const result = test();
		if (!result) {
			errors.push(`${name} not available`);
		}
		return result;
	} catch {
		errors.push(`${name} not available`);
		return false;
	}
}

function testStorage(storage: Storage, name: string, errors: string[]): boolean {
	return testFeature(name, () => {
		storage.setItem('test', 'test');
		storage.removeItem('test');
		return true;
	}, errors);
}

function testGlobalType(name: string, globalName: string, errors: string[]): boolean {
	return testFeature(name, () => typeof (window as unknown as Record<string, unknown>)[globalName] !== 'undefined', errors);
}

function testCanvasContext(contextType: string): boolean {
	try {
		const canvas = document.createElement('canvas');
		return !!(canvas.getContext(contextType) || canvas.getContext(`experimental-${contextType}`));
	} catch {
		return false;
	}
}

function testCSSProperty(property: string, value: string): boolean {
	try {
		const el = document.createElement('div');
		el.style.setProperty(property, value);
		return el.style.getPropertyValue(property) === value;
	} catch {
		return false;
	}
}

function testDisplayMode(mode: string): boolean {
	try {
		const el = document.createElement('div');
		el.style.display = mode;
		return el.style.display === mode;
	} catch {
		return false;
	}
}

export function detectBrowserCompatibility(): BrowserInfo {
	const errors: string[] = [];
	const nav = navigator;

	const features = {
		localStorage: testStorage(localStorage, 'localStorage', errors),
		sessionStorage: testStorage(sessionStorage, 'sessionStorage', errors),
		fetch: testGlobalType('fetch', 'fetch', errors),
		promise: testGlobalType('Promise', 'Promise', errors),
		urlSearchParams: testGlobalType('URLSearchParams', 'URLSearchParams', errors),
		webSocket: testGlobalType('WebSocket', 'WebSocket', errors),
		webGL: testFeature('WebGL', () => testCanvasContext('webgl'), errors),
		canvas: testFeature('Canvas', () => testCanvasContext('2d'), errors),
		svg: testFeature('SVG', () => !!(
			document.createElementNS && 
			document.createElementNS('http://www.w3.org/2000/svg', 'svg')
		), errors),
		flexbox: testFeature('CSS Flexbox', () => testDisplayMode('flex'), errors),
		grid: testFeature('CSS Grid', () => testDisplayMode('grid'), errors),
		cssVariables: testFeature('CSS Variables', () => testCSSProperty('--test', 'value'), errors),
	};

	return {
		userAgent: nav.userAgent || 'Unknown',
		platform: nav.platform || 'Unknown',
		vendor: nav.vendor || 'Unknown',
		language: nav.language || 'Unknown',
		cookieEnabled: nav.cookieEnabled || false,
		onLine: nav.onLine || false,
		javaEnabled: nav.javaEnabled ? nav.javaEnabled() : false,
		features,
		errors,
	};
}

export function logBrowserCompatibility(): void {
	if (process.env.NODE_ENV !== 'development') {
		return;
	}

	const info = detectBrowserCompatibility();

	if (info.errors.length > 0) {
		info.errors.forEach(error => console.warn(error));
	}
}

export function isHuaweiDevice(): boolean {
	const ua = navigator.userAgent.toLowerCase();
	return ua.includes('huawei') || ua.includes('honor') || ua.includes('emui');
}

export function isOldAndroid(): boolean {
	const match = navigator.userAgent.match(/android\s+(\d+\.?\d*)/i);
	return match ? parseFloat(match[1]) < 7.0 : false;
}
